import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import api, { getAllSites, getEvidenceDisplayUrl } from '../../../lib/api';
import { formatDisplayLong } from '../../../lib/dateUtils';
import { buildingRoomDisplay } from '../../../lib/formatUtils';
import { ImageModal } from '../../../components/ImageModal';
import Badge from '../../../components/ui/Badge';

const TEST_RESULTS = [
  { value: 'pass', label: 'Pass' },
  { value: 'fail', label: 'Fail' },
  { value: 'needs_repair', label: 'Needs Repair' },
];

export function TestHistoryView() {
  const [searchParams] = useSearchParams();
  const preselectedAssetId = searchParams.get('assetId') ?? '';

  const [selectedImage, setSelectedImage] = useState(null);
  const [filters, setFilters] = useState({
    assetId: preselectedAssetId,
    siteId: '',
    result: '',
    startDate: '',
    endDate: '',
  });

  // Sync assetId filter when URL changes (e.g. deep link from Assets History)
  useEffect(() => {
    const assetId = searchParams.get('assetId') ?? '';
    setFilters((prev) => (prev.assetId !== assetId ? { ...prev, assetId } : prev));
  }, [searchParams]);

  const queryParams = new URLSearchParams();
  if (filters.assetId) queryParams.set('assetId', filters.assetId);
  if (filters.siteId) queryParams.set('siteId', filters.siteId);
  if (filters.result) queryParams.set('result', filters.result);
  if (filters.startDate) queryParams.set('startDate', filters.startDate);
  if (filters.endDate) queryParams.set('endDate', filters.endDate);

  const [testsPage, setTestsPage] = useState(1);

  const setFiltersAndReset = (updater) => {
    setFilters(updater);
    setTestsPage(1);
  };

  const { data: sites = [], error: sitesError } = useQuery(
    ['sites'],
    () => getAllSites(),
  );

  const {
    data: testsResponse,
    isLoading: historyLoading,
    error: historyError,
  } = useQuery(
    ['tests', filters.assetId, filters.siteId, filters.result, filters.startDate, filters.endDate, testsPage],
    () => {
      queryParams.set('page', testsPage);
      queryParams.set('limit', '50');
      return api.get(`/tests?${queryParams.toString()}`).then((res) => res.data);
    },
  );
  const historyTests = testsResponse?.tests ?? null;
  const testsPagination = testsResponse?.pagination ?? null;

  if (historyLoading) return <div className="page-loading">Loading...</div>;
  if (historyError) {
    const isNetworkError = historyError.code === 'ERR_NETWORK' || !historyError.response;
    return (
      <div className="page-error">
        {isNetworkError ? (
          <>
            <p><strong>Cannot connect to the server.</strong></p>
            <p>Make sure the backend is running (e.g. <code>npm run dev:backend</code> on port 3000).</p>
          </>
        ) : (
          <p>Failed to load test results.</p>
        )}
      </div>
    );
  }

  return (
    <>
      <div className="filters">
        {sitesError && (
          <p className="text-muted" style={{ width: '100%', marginBottom: '0.5rem' }}>
            Site filters are temporarily unavailable.
          </p>
        )}
        <input
          type="number"
          placeholder="Asset ID"
          value={filters.assetId}
          onChange={(e) => setFiltersAndReset((f) => ({ ...f, assetId: e.target.value }))}
        />
        <select
          value={filters.siteId}
          onChange={(e) => setFiltersAndReset((f) => ({ ...f, siteId: e.target.value }))}
        >
          <option value="">All Sites</option>
          {sites.map((s) => (
            <option key={s.id} value={s.id}>
              {s.display_name ?? s.name ?? s.site_code ?? 'Unnamed site'}
            </option>
          ))}
        </select>
        <select
          value={filters.result}
          onChange={(e) => setFiltersAndReset((f) => ({ ...f, result: e.target.value }))}
        >
          <option value="">All Results</option>
          {TEST_RESULTS.map((r) => (
            <option key={r.value} value={r.value}>
              {r.label}
            </option>
          ))}
        </select>
        <input
          type="date"
          placeholder="Start Date"
          value={filters.startDate}
          onChange={(e) => setFiltersAndReset((f) => ({ ...f, startDate: e.target.value }))}
        />
        <input
          type="date"
          placeholder="End Date"
          value={filters.endDate}
          onChange={(e) => setFiltersAndReset((f) => ({ ...f, endDate: e.target.value }))}
        />
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Site</th>
              <th>Asset</th>
              <th>Asset Type</th>
              <th>Test Type</th>
              <th>Result</th>
              <th>Evidence</th>
              <th>Tested By</th>
            </tr>
          </thead>
          <tbody>
            {historyTests?.map((test) => (
              <tr key={test.id}>
                <td>
                  {test.test_date ? formatDisplayLong(test.test_date) : '-'}
                </td>
                <td>{test.site_name ?? '-'}</td>
                <td>{buildingRoomDisplay(test.building, test.room) !== '-' ? buildingRoomDisplay(test.building, test.room) : (test.location_description ?? '-')}</td>
                <td>{test.asset_type ?? '-'}</td>
                <td>{test.test_type ?? '-'}</td>
                <td>
                  <Badge
                    variant={
                      test.result === 'fail' ? 'danger'
                        : test.result === 'pass' ? 'success'
                          : test.result === 'needs_repair' ? 'warning'
                            : 'muted'
                    }
                  >
                    {TEST_RESULTS.find((r) => r.value === test.result)?.label ?? (test.result || '-')}
                  </Badge>
                </td>
                <td>
                  {(test.photo_url || test.photoUrl) ? (
                    <button
                      type="button"
                      className="evidence-thumb-btn"
                      onClick={() => setSelectedImage(getEvidenceDisplayUrl(test.photo_url || test.photoUrl))}
                      title="View photo"
                    >
                      <img
                        src={getEvidenceDisplayUrl(test.photo_url || test.photoUrl)}
                        alt="Evidence"
                        className="evidence-thumb"
                        loading="lazy"
                      />
                    </button>
                  ) : (
                    <span className="text-muted">—</span>
                  )}
                </td>
                <td>{test.tested_by_name ?? '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {(!historyTests || historyTests.length === 0) && (
          <p className="empty-state">No test results found.</p>
        )}
      </div>

      {testsPagination && testsPagination.totalPages > 1 && (
        <div className="pagination-controls">
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => setTestsPage((p) => Math.max(p - 1, 1))}
            disabled={testsPagination.currentPage <= 1}
          >
            Previous
          </button>
          <span className="pagination-info">
            Page {testsPagination.currentPage} of {testsPagination.totalPages}
          </span>
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => setTestsPage((p) => p + 1)}
            disabled={!testsPagination.hasNextPage}
          >
            Next
          </button>
        </div>
      )}

      <ImageModal
        src={selectedImage}
        alt="Evidence"
        onClose={() => setSelectedImage(null)}
      />
    </>
  );
}
